// src/modules/assetManagement/management/AssetManagementPage.tsx
// Asset Management: look after the files themselves. Upload, archive, share, unlink and delete only happen here.
import React from "react";
import { useSearchParams } from "react-router-dom";
import { FolderCog, History, ListChecks, Upload } from "lucide-react";
import { assetApi } from "../assetApi";
import { Button } from "../components/ui";
import { useAssetQuery } from "../useAssetQuery";
import AssetUpload from "./AssetUpload";
import ManageAssets from "./ManageAssets";
import UploadLog from "./UploadLog";
import { useSharedUploadQueue } from "./UploadQueueContext";

type Tab = "assets" | "upload" | "log";

const AssetManagementPage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const tab = (["assets", "upload", "log"].includes(params.get("tab") ?? "") ? params.get("tab") : "assets") as Tab;
  const queue = useSharedUploadQueue();
  const { data: log = [] } = useAssetQuery(() => assetApi.listUploadLog(), []);
  const openFailures = new Set(log.filter((r) => r.resolution === "open").map((r) => r.fileKey)).size;
  const activeUploads = queue.items.filter((i) => i.status === "uploading" || i.status === "processing").length;

  const setTab = (t: Tab) => {
    const next = new URLSearchParams(params);
    if (t === "assets") next.delete("tab");
    else next.set("tab", t);
    setParams(next);
  };

  const tabs: { id: Tab; label: string; Icon: typeof Upload; badge?: React.ReactNode }[] = [
    { id: "assets", label: "Assets", Icon: ListChecks },
    {
      id: "upload",
      label: "Upload",
      Icon: Upload,
      badge: activeUploads > 0 ? <span className="ml-1 px-1.5 rounded-full bg-emerald-100 text-emerald-700 text-[11px]">{activeUploads}</span> : null,
    },
    {
      id: "log",
      label: "Upload log",
      Icon: History,
      badge: openFailures > 0 ? <span className="ml-1 px-1.5 rounded-full bg-red-100 text-red-700 text-[11px]">{openFailures}</span> : null,
    },
  ];

  return (
    <div className="max-w-[1500px] mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-slate-200 text-slate-700 flex items-center justify-center">
            <FolderCog size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Asset Management</h1>
            <p className="text-sm text-slate-500">Upload, fix, archive, share and delete your files</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {tab !== "upload" && (
            <Button variant="primary" icon={<Upload size={16} />} onClick={() => setTab("upload")}>
              Upload assets
            </Button>
          )}
        </div>
      </div>

      <nav className="flex gap-6 border-b border-slate-200 mb-5" aria-label="Asset Management sections">
        {tabs.map(({ id, label, Icon, badge }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`-mb-px inline-flex items-center gap-2 pb-3 pt-1 text-sm font-semibold border-b-2 transition ${
              tab === id ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            <Icon size={16} />
            {label}
            {badge}
          </button>
        ))}
      </nav>

      {tab === "assets" && <ManageAssets onUpload={() => setTab("upload")} />}
      {tab === "upload" && <AssetUpload queue={queue} onViewLog={() => setTab("log")} />}
      {tab === "log" && <UploadLog queue={queue} onRetried={() => setTab("upload")} />}
    </div>
  );
};

export default AssetManagementPage;
